import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Braces, Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { withScheme } from '@/utils/url'

/** A placeholder the ad server fills in when it calls the tracker. */
export interface TrackingUrlMacro {
  macro: string
  label: string
  description: string
}

export interface TrackingUrlListInputProps {
  value: string[]
  onChange: (value: string[]) => void
  /** Placeholders offered in each row's insert menu. */
  macros: TrackingUrlMacro[]
  /** No more rows can be added once the list holds this many. */
  max?: number
  addLabel: string
  placeholder?: string
  /** Focuses the last row on mount, for a list opened by an "add" button. */
  autoFocus?: boolean
}

/**
 * An editable list of third-party tracking URLs, one input per row. Each row
 * has a menu that puts a placeholder such as `[timestamp]` where the caret
 * is, so nobody needs to remember the exact spelling. A scheme is filled in
 * when a row loses focus, unless the row holds a script tag.
 */
export function TrackingUrlListInput({
  value,
  onChange,
  macros,
  max,
  addLabel,
  placeholder,
  autoFocus,
}: TrackingUrlListInputProps) {
  const { t } = useTranslation()
  const inputs = useRef<(HTMLInputElement | null)[]>([])
  // The row to focus after the next render, once it exists in the DOM.
  const [focusRow, setFocusRow] = useState<{ index: number; caret?: number } | null>(
    autoFocus && value.length > 0 ? { index: value.length - 1 } : null
  )

  useEffect(() => {
    if (!focusRow) return
    const input = inputs.current[focusRow.index]
    if (input) {
      input.focus()
      const caret = focusRow.caret ?? input.value.length
      input.setSelectionRange(caret, caret)
    }
    setFocusRow(null)
  }, [focusRow])

  const setRow = (index: number, next: string) =>
    onChange(value.map((v, i) => (i === index ? next : v)))

  const insertMacro = (index: number, macro: string) => {
    const current = value[index] ?? ''
    const input = inputs.current[index]
    const start = input?.selectionStart ?? current.length
    const end = input?.selectionEnd ?? current.length
    setRow(index, current.slice(0, start) + macro + current.slice(end))
    setFocusRow({ index, caret: start + macro.length })
  }

  const canAdd = max === undefined || value.length < max

  return (
    <div className="flex flex-col gap-2">
      {value.map((url, index) => (
        <InputGroup key={index}>
          <InputGroupInput
            ref={(el) => {
              inputs.current[index] = el
            }}
            value={url}
            placeholder={placeholder}
            aria-label={`${addLabel} ${index + 1}`}
            spellCheck={false}
            onChange={(e) => setRow(index, e.target.value)}
            onBlur={(e) => {
              const raw = e.target.value
              const normalized = raw.trim().startsWith('<') ? raw.trim() : withScheme(raw)
              if (normalized !== raw) setRow(index, normalized)
            }}
          />
          <InputGroupAddon align="inline-end">
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <InputGroupButton size="icon-xs" aria-label={t('common.insertPlaceholder')}>
                      <Braces />
                    </InputGroupButton>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>{t('common.insertPlaceholder')}</TooltipContent>
              </Tooltip>
              {/* Focus stays in the input the placeholder went into, not on the menu button. */}
              <DropdownMenuContent
                align="end"
                className="w-72"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <DropdownMenuLabel>{t('common.insertPlaceholder')}</DropdownMenuLabel>
                {macros.map((m) => (
                  <DropdownMenuItem
                    key={m.macro}
                    onSelect={() => insertMacro(index, m.macro)}
                    className="flex-col items-start gap-0.5"
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      <span>{m.label}</span>
                      <code className="font-mono text-xs text-muted-foreground">{m.macro}</code>
                    </span>
                    <span className="text-xs text-muted-foreground">{m.description}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <InputGroupButton
              size="icon-xs"
              aria-label={t('common.remove')}
              onClick={() => onChange(value.filter((_, i) => i !== index))}
            >
              <X />
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
      ))}
      {canAdd && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => {
            onChange([...value, ''])
            setFocusRow({ index: value.length })
          }}
        >
          <Plus className="size-3.5" />
          {addLabel}
        </Button>
      )}
    </div>
  )
}
